import { useState, useEffect, useRef } from "react"
import { Form, Row, Col } from 'react-bootstrap'
import { useNavigate, Link } from "react-router-dom"
import { useQuery, useLazyQuery, useMutation } from "@apollo/client"
import CustomModal from '../../../../components/CustomModal'
import CustomButton from '../../../../components/CustomButton'
import useUser from "../../../../hooks/useUser"

import "./index.scss"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import GET_PROGRAMMES from "../../../../graphql/queries/courseCatalogue/getProgrammes"
import GET_PROGRAMME_FORM from "../../../../graphql/queries/courseCatalogue/getProgrammeForm"

import CREATE_PROGRAMME from '../../../../graphql/mutations/courseCatalogue/createProgramme'
import EDIT_PROGRAMME from '../../../../graphql/mutations/courseCatalogue/editProgramme'
import REMOVE_PROGRAMME from '../../../../graphql/mutations/courseCatalogue/removeProgramme'

import CustomTable from "../Table"
import TableSkeleton from "../Table/components/TableSkeleton"

import toastConfiguration from "../../../../data/toastConfiguration"
import { ToastContainer, toast } from "react-toastify"

import IconButton from "../../../../components/IconButton"
import {
  faPlus,
  faCheck,
  faFloppyDisk,
  faTrash,
  faChevronLeft,
  faUpload
} from "@fortawesome/free-solid-svg-icons"
import COLORS from "../../../../data/colors"

import { useTranslation } from 'react-i18next'

import DynamicForm from '../../../../components/DynamicForm'

import { 
  convertProgrammeStringToNumbers,
  isDeepEqual,
  getPatchDiff,
  processForm,
  processTableHeader
} from '../../../../utils/courseCatalogueManager'


const Programmes = () => {
  const formRef = useRef(null)
  const [loadingComponent, setLoadingComponent] = useState(true)
  const [programmeData, setProgrammeData] = useState([])
  const [programmeForm, setProgrammeForm] = useState([])
  const [selectedProgramme, setSelectedProgramme] = useState({})
  const [tableHeaders, setTableHeaders] = useState([])
  const [columnsPerFieldMap, setColumnsPerFieldMap] = useState({})
  const [formData, setFormData] = useState({})
  const [validated, setValidated] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState("")
  const [page, setPage] = useState(localStorage.getItem('pageProgrammes') !== null ? parseInt(JSON.parse(localStorage.getItem('pageProgrammes'))) : 0)
  const [fieldOptions, setFieldOptions] = useState({})

  const navigate = useNavigate()
  const { t } = useTranslation('translation', {keyPrefix: 'courseCatalogue.programmesSection'})
  const { te } = useTranslation()
  const { user } = useUser()

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [loadProgrammes, { 
    loading: loadingProgrammes, 
    data: dataProgrammes,
    errorProgrammes,
    refetch: refetchProgrammes }
  ] = useLazyQuery(GET_PROGRAMMES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programmes data received")

      setProgrammeData(data.programmes)
    },
    onError: (error) => {
      console.error("Error loading Programmes")
    }
  })

  const [loadGetProgrammeForm, { 
    loading: loadingGetProgrammeForm, dataGetProgrammeForm, errorGetProgrammeForm }
  ] = useLazyQuery(GET_PROGRAMME_FORM, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programme Form data received")

      // Save form into session storage
      sessionStorage.setItem("programmeForm", JSON.stringify(data.getProgrammeForm))

      setProgrammeForm(data.getProgrammeForm)

      // Get table headers list from form display
      const { headers, columnsHeaders } = processTableHeader(data.getProgrammeForm)

      setTableHeaders(headers)
      setColumnsPerFieldMap(columnsHeaders)

      // Save the fields form schema with type select in a separate object
      const fieldOptionsObj = processForm(data.getProgrammeForm)
      setFieldOptions(fieldOptionsObj)

    },
    onError: (error) => {
      console.error("Error loading Programme Form")
    }
  })

  const [callCreateProgramme, { 
    loading: loadingCreateProgramme, dataCreateProgramme }
  ] = useMutation(CREATE_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme created")
    },
    onError: (error) => {
      console.error("Error creating Programme")
    }
  })

  const [callEditProgramme, { 
    loading: loadingEditProgramme, dataEditProgramme }
  ] = useMutation(EDIT_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme modified")
    },
    onError: (error) => {
      console.error("Error editing Programme")
    }
  })

  const [callRemoveProgramme, { 
    loading: loadingRemoveProgramme, dataRemoveProgramme }
  ] = useMutation(REMOVE_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme removed")
    },
    onError: (error) => {
      console.error("Error removing Programme")
    }
  })

  useEffect(() => {

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))

    if(!userHasPermission) {
      navigate('/cc')
      return
    }

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections, redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const programmeForm = JSON.parse(sessionStorage.getItem('programmeForm'))

    loadProgrammes({
      variables: {
        page,
        schacCode: user?.heiID,
        baseUrl
      }
    })

    if(!programmeForm){
      loadGetProgrammeForm({
        variables: { baseUrl, apiKey }
      })
    } else {
      
      setProgrammeForm(programmeForm)

      // Get table headers list from form display
      const { headers, columnsHeaders } = processTableHeader(programmeForm)

      setTableHeaders(headers)
      setColumnsPerFieldMap(columnsHeaders)

      // Save the fields form schema with type select in a separate object
      const fieldOptionsObj = processForm(programmeForm)
      setFieldOptions(fieldOptionsObj)

    }

  }, [loadProgrammes])

  useEffect(() => {
    if (dataProgrammes?.programmes && programmeForm && Object.keys(programmeForm).length > 0) {
      /* setProgrammeData(dataProgrammes.programmes) */
      setLoadingComponent(false)
    }
  }, [dataProgrammes, programmeForm])

  useEffect(() => {
    page !== null && localStorage.setItem('pageProgrammes', JSON.stringify(page))
    fetchProgrammes()
  }, [page])

  const fetchProgrammes = () => {
    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const baseUrl = backendInstanceStorage?.baseUrl

    loadProgrammes({
      variables: {
        page,
        schacCode: user?.heiID,
        baseUrl
      }
    })
  }

  const handleFormChange = (data) => {
    setFormData(data)
  }

  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleAdd = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading(t('addProgramme.loading'), toastConfiguration)

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey
    const backendHeiUuid = heiConnectionStorage?.backendHeiUuid

    const cleanedData = convertProgrammeStringToNumbers(formData)

    try {
      let result = {}

      result = await callCreateProgramme({
        variables: {
          programmeData: cleanedData,
          baseUrl: baseUrl,
          apiKey: apiKey,
          heiUuid: backendHeiUuid
        }
      })

      setValidated(false)
      if (!result?.errors) {

        toast.update(toastId, {
          ...toastConfiguration,
          render: t('addProgramme.success') + cleanedData.title[0].string,
          type: "success",
          isLoading: false,
        })
        const { data: refreshedProgramme } = await refetchProgrammes({
          variables: {
            page,
            schacCode: user?.heiID,
            baseUrl
          }
        })

        setProgrammeData(refreshedProgramme.programmes)

        setShowModal(false)

      } else {

        console.error("Create Programme failed.")

        toast.update(toastId, {
          ...toastConfiguration,
          render: t('addProgramme.failure1') + result?.errors,
          type: "error",
          isLoading: false,
        })
      }

    } catch (err) {
      console.error("Create Programme failed:", err)

      toast.update(toastId, {
        ...toastConfiguration,
        render: t('addProgramme.failure2'),
        type: "error",
        isLoading: false,
      })
    }

  }

  const handleEdit = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading(t('editProgramme.loading'), toastConfiguration)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    let cleanedData = convertProgrammeStringToNumbers(formData)
    cleanedData.id = selectedProgramme.id
    cleanedData.uuid = selectedProgramme.uuid
    cleanedData.status = selectedProgramme.status

    // Check if the data has changed
    // If yes, run the query
    // if not, show an error message
    if(!isDeepEqual(selectedProgramme, cleanedData)) {
      try {
        let result = {}

        result = await callEditProgramme({
          variables: {
            programmeData: getPatchDiff(selectedProgramme, cleanedData),
            baseUrl,
            apiKey
          }
        })

        setValidated(false)
        
        if (!result?.errors) {

          toast.update(toastId, {
            ...toastConfiguration,
            render: t('editProgramme.success.part1') + cleanedData.title[0].string + t('editProgramme.success.part2'),
            type: "success",
            isLoading: false,
          })
          
          const { data: refreshedProgramme } = await refetchProgrammes({
            variables: {
              page,
              schacCode: user?.heiID,
              baseUrl            }
          })

          setProgrammeData(refreshedProgramme.programmes)

          setShowModal(false)

        } else {

          console.error("Edit Programme failed.")

          toast.update(toastId, {
            ...toastConfiguration,
            render: t('editProgramme.failure1') + result?.errors,
            type: "error",
            isLoading: false,
          })
        }

      } catch (err) {
        
        console.error("Edit Programme failed:", err)

        toast.update(toastId, {
          ...toastConfiguration,
          render: t('editProgramme.failure2'),
          type: "error",
          isLoading: false,
        })
      }

    } else {
      toast.update(toastId, {
        ...toastConfiguration,
        render: t('editProgramme.noChanges'),
        type: "error",
        isLoading: false,
      })
    }

  }

  const handleDelete = async (e) => {
    
    setShowModal(false)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const toastId = toast.loading(t('deleteProgramme.loading'), toastConfiguration)
    
    try {

      const result = await callRemoveProgramme({
       variables: {
          baseUrl: baseUrl,
          apiKey: apiKey,
          programmeUuid: selectedProgramme.id,
        }
      })

      setValidated(false)
      
      if (!result?.errors) {
        toast.update(toastId, {
          ...toastConfiguration,
          render: t('deleteProgramme.success'),
          type: "success",
          isLoading: false,
        })
        const { data: refreshedProgramme } = await refetchProgrammes({
          variables: {
            page,
            schacCode: user?.heiID,
            baseUrl,
          }
        })

        setProgrammeData(refreshedProgramme.programmes)
        
        setShowModal(false)

      } else {

        console.error("Unpublish Programme failed.")

        toast.update(toastId, {
          ...toastConfiguration,
          render: t('deleteProgramme.failure') + result?.errors,
          type: "error",
          isLoading: false,
        })
      }
    } catch (err) {

      toast.update(toastId, {
        ...toastConfiguration,
        render: t('deleteProgramme.failure'),
        type: "error",
        isLoading: false,
      })

    }
  }

  return (
    <main className="h-auto text-center mt-4">
      {loadingProgrammes || loadingGetProgrammeForm || loadingComponent ? <TableSkeleton
        columnsPerFieldMap={columnsPerFieldMap}
      /> : errorProgrammes ?
        <div className="text-center text-danger mx-auto">{te('errorMessage')}</div> :
        <>
          <Row className="my-5 justify-content-between align-items-center">
            <Col md={2} className="my-4">
              <Link to="/cc" className="goBackLink">
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  color={COLORS.greyText}
                  className="with-icon me-1"
                  id={"smallIcon"}
                />
                {t('goBack')}
              </Link>
            </Col>
            <Col xs={12} md={4} className="my-2 d-flex flex-column flex-md-row gap-2 justify-content">
              <CustomButton 
                buttonType={"primary"} 
                small={true} 
                text={t('upload')} 
                hasIcon={true} 
                iconColor={COLORS.white} 
                icon={faUpload} 
                handleClick={(e) => { navigate("/cc/programmes/upload") }}
              />
              <CustomButton 
                type="submit" 
                buttonType={"primary"} 
                text={t('addNewProgramme')} 
                icon={faPlus} 
                handleClick={() => {
                  setSelectedAction("add")
                  setValidated(false)
                  setShowModal(true)
                }} 
                hasIcon 
                small 
              />
            </Col>
          </Row>
          <CustomTable
            type={"programmes-"}
            columnsPerFieldMap={columnsPerFieldMap}
            hasPagination={true}
            pageLimit={30}
            page={page}
            hasFilters={false}
            setPage={(p) => setPage(p)}
            totalCount={dataProgrammes?.programmes.count}
            rowClassName="programmes-listing-table-row"
            headerClassName="programmes-listing-table-header"
            data={dataProgrammes?.programmes?.programmeList}
            tableHead={tableHeaders}
            fieldOptions={fieldOptions}
            onClickHandlers={programmeData?.programmeList.map(
              ( programme ) =>
              (e) => {
                navigate("/cc/programmes/" + programme.id)
              }
            )}
          >
            {dataProgrammes?.programmes?.programmeList.map((programme) => (
              <div
                key={programme?.id}
                className={`py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1`}
              >
                {/* <Toggle/> */}
                <IconButton
                  variant="edit"
                  title={t('saveChanges')}
                  onClick={() => {
                    setSelectedAction("edit")
                    setShowModal(true)
                    setSelectedProgramme(programme)
                  }}
                />
                <IconButton
                  variant="delete"
                  onClick={() => {
                    setSelectedAction("delete")
                    setShowModal(true)
                    setSelectedProgramme(programme)
                  }}
                  title={t('delete')}
                />
              </div>
            ))}
          </CustomTable>
          <CustomModal
            show={showModal}
            handleClose={() => {
              setShowModal(false)
              setValidated(false)
            }}
            disabledCancelButton={
              loadingCreateProgramme || loadingEditProgramme
            }
            body={
              (selectedAction === "add" || selectedAction === "edit") && (
                <div>
                  
                  <Form ref={formRef} noValidate validated={validated}>
                    <DynamicForm 
                      schema={programmeForm}
                      onChange={handleFormChange}
                      defaultValues={selectedAction === 'edit' ? selectedProgramme : {}}
                    />
                  </Form>
                  
                </div>
              )
            }
            button={
              selectedAction === "add" ? ( // new
                <CustomButton
                  buttonType={"primary"}
                  disabled={loadingCreateProgramme}
                  text={t('add')}
                  icon={faCheck}
                  hasIcon={true}
                  iconColor={COLORS.white}
                  handleClick={handleAdd}
                />
              ) : selectedAction === "edit" ? ( // edit
                <CustomButton
                  buttonType={"primary"}
                  disabled={loadingEditProgramme}
                  text={t('saveChanges')}
                  icon={faFloppyDisk}
                  hasIcon={true}
                  iconColor={COLORS.white}
                  handleClick={handleEdit}
                />
              ) : (
                // delete
                <CustomButton
                  buttonType={"decline"}
                  text={t('unpublish')}
                  iconColor={COLORS.white}
                  handleClick={handleDelete}
                />
              )
            }
            title={
              selectedAction === "add" ? (
                t('addAction.title')
              ) : selectedAction === "edit" ? (
                t('editAction.title')
              ) : (
                <>
                  {t('deleteAction.title.part1')}
                  <span className="accounts-and-access-delete-text-on-modal">
                    {t('deleteAction.title.part2')}
                  </span>{" "}
                  {t('deleteAction.title.part3')}
                </>
              )
            }
            subtitle={
              selectedAction === "add" ? (
                t('addAction.subtitle')
              ) : selectedAction === "edit" ? (
                t('editAction.subtitle')
              ) : (t('deleteAction.subtitle'))
            }
            hasIcon
            fullscreen
          />
        </>}
          <ToastContainer
            position="bottom-left"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
    </main>
  )
}

export default Programmes
